import React, { useCallback, useEffect, useState } from 'react';
import {
  approveWithdrawRequest,
  fetchAdminWithdrawRequests,
  rejectWithdrawRequest,
} from '../../services/walletApi';
import {
  TreasuryUltraAlert,
  TreasuryUltraBtn,
  TreasuryUltraField,
  TreasuryUltraInput,
  TreasuryUltraRow,
  TreasuryUltraShell,
} from './TreasuryUltraShell';

export default function WithdrawApprovals({ onQueueChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [forms, setForms] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminWithdrawRequests('pending');
      const list = data.items || [];
      setItems(list);
      if (onQueueChange) onQueueChange(list.length);
    } catch {
      setItems([]);
      if (onQueueChange) onQueueChange(0);
    } finally {
      setLoading(false);
    }
  }, [onQueueChange]);

  useEffect(() => {
    load();
  }, [load]);

  const updateForm = (id, patch) => {
    setForms((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const handleApprove = async (id) => {
    const form = forms[id] || {};
    const utr = (form.utr || '').trim();
    if (!utr) {
      alert('NEFT/RTGS UTR is required');
      return;
    }
    if (!form.proofFile) {
      alert('Upload NEFT proof (screenshot or PDF)');
      return;
    }
    setBusyId(id);
    try {
      const result = await approveWithdrawRequest(id, {
        utr,
        note: form.note || '',
        proofFile: form.proofFile,
      });
      if (result.pendingSecondApproval) {
        alert('UTR + proof saved. A different admin must approve in Maker–Checker panel.');
      }
      setForms((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await load();
    } catch (err) {
      alert(err.message || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id) => {
    const note = window.prompt('Rejection note (optional):') || '';
    setBusyId(id);
    try {
      await rejectWithdrawRequest(id, note);
      await load();
    } catch (err) {
      alert(err.message || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <p className="text-sm text-de-muted">Loading withdraw requests…</p>;
  }

  return (
    <TreasuryUltraShell
      title="Owner Withdraw Requests"
      subtitle="UTR + proof file mandatory. Large amounts need second admin approval."
      icon={String.fromCodePoint(0x1F4B2)}
      accent="payout"
      badge={items.length || '0'}
      scrollable={items.length > 2}
      scrollMaxHeightClass="max-h-[min(420px,48vh)]"
      showScrollHint={items.length > 2}
    >
      {items.length === 0 ? (
        <TreasuryUltraAlert tone="info">No pending owner withdraw requests.</TreasuryUltraAlert>
      ) : (
        items.map((row) => (
          <TreasuryUltraRow
            key={row.id}
            tone={row.status === 'awaiting_second_approval' ? 'amber' : 'sky'}
            title={`₹${Number(row.amount).toLocaleString('en-IN')}${row.users?.name ? ` · ${row.users.name}` : ''}`}
            meta={`${row.status === 'awaiting_second_approval' ? 'Awaiting 2nd admin · ' : ''}${row.bank_name || 'Bank'} ${row.account_last4 ? `****${row.account_last4}` : ''}${row.payment_utr ? ` · UTR ${row.payment_utr}` : ''}`}
            actions={row.status === 'awaiting_second_approval' ? (
              <TreasuryUltraAlert tone="warn">Use Maker–Checker panel</TreasuryUltraAlert>
            ) : (
              <div className="w-full space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <TreasuryUltraField label="NEFT / RTGS UTR" hint="8–22 characters">
                    <TreasuryUltraInput
                      value={forms[row.id]?.utr || ''}
                      onChange={(e) => updateForm(row.id, { utr: e.target.value })}
                      placeholder="UTR from bank"
                      maxLength={22}
                    />
                  </TreasuryUltraField>
                  <TreasuryUltraField label="UTR proof" hint="PDF, JPG, PNG (max 5MB)">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                      className="de-treasury-ultra-input w-full text-xs file:mr-2 file:rounded file:border-0 file:bg-de-gold/20 file:px-2 file:py-1 file:text-de-gold"
                      onChange={(e) => updateForm(row.id, { proofFile: e.target.files?.[0] || null })}
                    />
                  </TreasuryUltraField>
                </div>
                <TreasuryUltraField label="Admin note" hint="Optional">
                  <TreasuryUltraInput
                    value={forms[row.id]?.note || ''}
                    onChange={(e) => updateForm(row.id, { note: e.target.value })}
                    placeholder="NEFT processed"
                    maxLength={200}
                  />
                </TreasuryUltraField>
                <div className="flex flex-wrap gap-2">
                  <TreasuryUltraBtn variant="payout" disabled={busyId === row.id} onClick={() => handleApprove(row.id)}>
                    Approve + UTR + Proof
                  </TreasuryUltraBtn>
                  <TreasuryUltraBtn variant="ghost" disabled={busyId === row.id} onClick={() => handleReject(row.id)}>
                    Reject
                  </TreasuryUltraBtn>
                </div>
              </div>
            )}
          />
        ))
      )}
    </TreasuryUltraShell>
  );
}
